import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiAlertTriangle, FiRotateCcw, FiLock, FiFileText, FiShield, FiCalendar, FiUser } from 'react-icons/fi';
import api from '../utils/api';

function fmtEuro(v) {
  return Number(v || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
}

export default function FirmarDocumentoPublico() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // Formulario de firma
  const [firmadoPorNombre, setFirmadoPorNombre] = useState('');
  const [firmadoPorNif, setFirmadoPorNif] = useState('');
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [enviandoFirma, setEnviandoFirma] = useState(false);
  const [errorFirma, setErrorFirma] = useState(null);

  // Canvas de firma
  const canvasRef = useRef(null);
  const dibujandoRef = useRef(false);
  const [hayTrazo, setHayTrazo] = useState(false);
  const [firmaGeneradaNombre, setFirmaGeneradaNombre] = useState('');

  useEffect(() => {
    cargarDatos();
  }, [token]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ajustarCanvas = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const ancho = Math.max(1, Math.round(rect.width));
      const alto = Math.max(1, Math.round(rect.height));

      canvas.width = ancho * ratio;
      canvas.height = alto * ratio;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(ratio, ratio);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 2.5;
    };

    ajustarCanvas();
    window.addEventListener('resize', ajustarCanvas);

    const admitePointerEvents = typeof window !== 'undefined' && 'PointerEvent' in window;
    const opcionesEvento = { passive: false };
    const eventos = admitePointerEvents
      ? [
        ['pointerdown', iniciarDibujo],
        ['pointermove', moverDibujo],
        ['pointerup', detenerDibujo],
        ['pointerleave', detenerDibujo],
        ['pointercancel', detenerDibujo],
      ]
      : [
        ['mousedown', iniciarDibujo],
        ['mousemove', moverDibujo],
        ['mouseup', detenerDibujo],
        ['mouseleave', detenerDibujo],
        ['touchstart', iniciarDibujo],
        ['touchmove', moverDibujo],
        ['touchend', detenerDibujo],
        ['touchcancel', detenerDibujo],
      ];

    eventos.forEach(([nombre, handler]) => canvas.addEventListener(nombre, handler, opcionesEvento));

    return () => {
      window.removeEventListener('resize', ajustarCanvas);
      eventos.forEach(([nombre, handler]) => canvas.removeEventListener(nombre, handler, opcionesEvento));
    };
  }, []);

  const cargarDatos = async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await api.get(`/firma/public/${token}`);
      setData(res.data);
      if (res.data?.cliente?.nombre && !firmadoPorNombre) {
        setFirmadoPorNombre(res.data.cliente.nombre);
      }
      if (res.data?.cliente?.nif && !firmadoPorNif) {
        setFirmadoPorNif(res.data.cliente.nif);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo obtener la información del documento.');
    } finally {
      setCargando(false);
    }
  };

  // Lógica del Canvas interactivo de firma
  const obtenerCoordenadas = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const punto = event.touches && event.touches[0]
      ? event.touches[0]
      : event.changedTouches && event.changedTouches[0]
        ? event.changedTouches[0]
        : event;

    const ratio = window.devicePixelRatio || 1;
    const anchoLogico = canvas.width / ratio;
    const altoLogico = canvas.height / ratio;
    const x = (punto.clientX - rect.left) * (anchoLogico / rect.width);
    const y = (punto.clientY - rect.top) * (altoLogico / rect.height);

    return {
      x: Math.min(Math.max(x, 0), anchoLogico),
      y: Math.min(Math.max(y, 0), altoLogico),
    };
  };

  const iniciarDibujo = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = obtenerCoordenadas(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';

    if (typeof canvas.setPointerCapture === 'function' && typeof e.pointerId === 'number') {
      try { canvas.setPointerCapture(e.pointerId); } catch { /* noop */ }
    }

    dibujandoRef.current = true;
    setHayTrazo(true);
  };

  const moverDibujo = (e) => {
    if (!dibujandoRef.current) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = obtenerCoordenadas(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const detenerDibujo = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    const canvas = canvasRef.current;
    if (canvas && typeof canvas.releasePointerCapture === 'function' && typeof e?.pointerId === 'number') {
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    }

    dibujandoRef.current = false;
  };

  const limpiarCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    dibujandoRef.current = false;
    setFirmaGeneradaNombre('');
    setHayTrazo(false);
  };

  const generarFirmaDesdeNombre = () => {
    const nombreBase = (firmadoPorNombre || '').trim();
    const valor = window.prompt('Escribe el nombre con el que quieres firmar', nombreBase || '');
    if (valor === null) return;

    const texto = String(valor).trim();
    if (!texto) {
      setErrorFirma('Debes introducir un nombre para generar la firma alternativa.');
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '600 42px "Segoe Script", "Bradley Hand", "Lucida Handwriting", cursive';
    const ancho = Math.min(canvas.width * 0.85, 560);
    const escala = Math.min(1, ancho / ctx.measureText(texto).width);
    const tamaño = Math.max(24, Math.min(72, 42 * escala));
    ctx.font = `600 ${tamaño}px "Segoe Script", "Bradley Hand", "Lucida Handwriting", cursive`;
    ctx.fillText(texto, canvas.width / 2, canvas.height / 2 + 8);
    setFirmaGeneradaNombre(texto);
    setHayTrazo(true);
    setErrorFirma(null);
  };

  const submitFirma = async (e) => {
    e.preventDefault();
    if (!hayTrazo) {
      setErrorFirma('Por favor, dibuje su firma con el ratón o el dedo en el recuadro blanco.');
      return;
    }
    if (!aceptaTerminos) {
      setErrorFirma('Debes aceptar expresamente los términos y condiciones de la firma digital.');
      return;
    }

    const canvas = canvasRef.current;
    const imagenFirma = canvas ? canvas.toDataURL('image/png') : '';

    setEnviandoFirma(true);
    setErrorFirma(null);

    try {
      const res = await api.post(`/firma/public/${token}`, {
        firmadoPorNombre: firmadoPorNombre.trim(),
        firmadoPorNif: firmadoPorNif.trim(),
        imagenFirma,
        aceptaTerminos: true,
      });

      setData((prev) => ({
        ...(prev || {}),
        ok: true,
        firmado: true,
        firmadoAt: res.data?.firmadoAt || new Date().toISOString(),
        firmadoPorNombre: firmadoPorNombre.trim(),
        firmadoPorNif: firmadoPorNif.trim(),
        ip: 'Registrada al firmar',
        imagenFirma,
        hashDocumento: res.data?.hashDocumento || '',
        yaFirmado: true,
      }));
    } catch (err) {
      setErrorFirma(err.response?.data?.error || 'Ocurrió un error al procesar la firma digital.');
    } finally {
      setEnviandoFirma(false);
    }
  };

  if (cargando) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', color: '#64748b' }}>
          <div className="trix-spinner" style={{ margin: '0 auto 12px' }} />
          <p>Cargando portal de firma digital...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: 20 }}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <FiAlertTriangle style={{ fontSize: 48, color: '#ef4444', marginBottom: 12 }} />
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 8px 0' }}>Enlace no disponible</h2>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 20 }}>{error || 'El enlace de firma es inválido o ha caducado.'}</p>
        </div>
      </div>
    );
  }

  const { empresa, cliente, documento, firmado, tipoDocumento } = data;
  const esPresupuesto = tipoDocumento === 'presupuesto';

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', padding: '24px 16px' }}>
      <div style={{ maxWidth: 840, margin: '0 auto' }}>

        {/* CABECERA CON LOGO Y NOMBRES */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {empresa?.logo ? (
              <img src={empresa.logo} alt={empresa.nombre} style={{ height: 44, borderRadius: 8, objectFit: 'contain' }} />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: 10, background: '#3b82f6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 20 }}>
                {empresa?.nombre ? empresa.nombre[0].toUpperCase() : 'T'}
              </div>
            )}
            <div>
              <h1 style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0, color: '#0f172a' }}>{empresa?.nombre || 'Trix Platform'}</h1>
              {empresa?.cif && <span style={{ fontSize: '0.82rem', color: '#64748b' }}>CIF: {empresa.cif}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span className="badge badge-info" style={{ fontSize: '0.85rem', fontWeight: 700 }}>
              {esPresupuesto ? 'PRESUPUESTO' : 'FACTURA'} N° {documento.numero}
            </span>
            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4 }}>
              Fecha: {new Date(documento.fecha).toLocaleDateString('es-ES')}
            </div>
          </div>
        </div>

        {/* ESTADO SI YA HA SIDO FIRMADO (SINGLE USE CADUCADO) */}
        {firmado ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ background: '#ffffff', border: '1px solid #86efac', borderRadius: 16, padding: 24, marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <FiCheckCircle style={{ fontSize: 32, color: '#16a34a' }} />
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#15803d', fontWeight: 700 }}>Documento Firmado Digitalmente</h3>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  Este enlace exclusivo de un solo uso ha caducado tras registrarse la firma.
                </span>
              </div>
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, display: 'grid', gap: 8, fontSize: '0.88rem' }}>
              <div><strong>Firmado por:</strong> {data.firmadoPorNombre} ({data.firmadoPorNif})</div>
              <div><strong>Fecha y hora:</strong> {data.firmadoAt ? new Date(data.firmadoAt).toLocaleString('es-ES') : '—'}</div>
              <div><strong>Dirección IP:</strong> {data.ip || 'Registrada'}</div>
              {data.hashDocumento && (
                <div style={{ wordBreak: 'break-all', fontSize: '0.8rem', color: '#64748b' }}>
                  <strong>Evidencia SHA-256:</strong> {data.hashDocumento}
                </div>
              )}
              {data.imagenFirma && (
                <div style={{ marginTop: 8 }}>
                  <strong>Trazo de firma registrado:</strong>
                  <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, padding: 8, width: 240, marginTop: 4 }}>
                    <img src={data.imagenFirma} alt="Firma registrada" style={{ width: '100%', height: 70, objectFit: 'contain' }} />
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: 14, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, color: '#1e40af', fontSize: '0.88rem' }}>
            <FiShield style={{ fontSize: 24, flexShrink: 0 }} />
            <span>
              <strong>Portal Oficial de Firma Digital.</strong> Este documento tiene validez jurídica legal conforme a la Ley 6/2020 de servicios electrónicos de confianza y el Reglamento eIDAS.
            </span>
          </div>
        )}

        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24, marginBottom: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 16px 0', borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
            Documento a Firmar
          </h3>

          <div style={{ marginBottom: 18, border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
            <iframe
              src={data.pdfPreviewUrl}
              title="Documento PDF"
              style={{ width: '100%', minHeight: 620, border: 'none', display: 'block', background: '#f8fafc' }}
            />
          </div>

          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 16px 0', borderBottom: '1px solid #f1f5f9', paddingBottom: 8 }}>
            Resumen del Documento
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20, fontSize: '0.88rem' }}>
            <div>
              <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Cliente</span>
              <strong>{cliente?.nombre || '—'}</strong>
              {cliente?.nif && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>NIF: {cliente.nif}</div>}
            </div>
            <div>
              <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Dirección</span>
              <div>{cliente?.direccion || '—'}</div>
              <div>{[cliente?.poblacion, cliente?.provincia].filter(Boolean).join(', ')}</div>
            </div>
            {documento.validez && (
              <div>
                <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Válido hasta</span>
                <strong>{new Date(documento.validez).toLocaleDateString('es-ES')}</strong>
              </div>
            )}
          </div>

          {/* TABLA DE LÍNEAS */}
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Descripción</th>
                  <th style={{ textAlign: 'right' }}>Cant.</th>
                  <th style={{ textAlign: 'right' }}>Precio Unit.</th>
                  <th style={{ textAlign: 'right' }}>IVA</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(documento.lineas || []).map((linea, i) => (
                  <tr key={i}>
                    <td>{linea.descripcion}</td>
                    <td style={{ textAlign: 'right' }}>{linea.cantidad}</td>
                    <td style={{ textAlign: 'right' }}>{fmtEuro(linea.precioUnitario)}</td>
                    <td style={{ textAlign: 'right' }}>{linea.iva}%</td>
                    <td style={{ textAlign: 'right' }}><strong>{fmtEuro(linea.total || (linea.cantidad * linea.precioUnitario))}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TOTALES */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 20, fontSize: '0.9rem', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
            <div>Base Imponible: <strong>{fmtEuro(documento.baseImponible)}</strong></div>
            <div>IVA: <strong>{fmtEuro(documento.totalIva)}</strong></div>
            <div style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: 800 }}>Total: {fmtEuro(documento.total)}</div>
          </div>
        </div>

        {/* FORMULARIO DE FIRMA DIGITAL (SOLO SI AÚN NO SE HA FIRMADO) */}
        {!firmado && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24 }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a' }}>
              <FiFileText style={{ color: '#3b82f6' }} /> Declaración y Firma Digital del Cliente
            </h3>

            {errorFirma && <div className="alert-error" style={{ marginBottom: 16 }}>{errorFirma}</div>}

            <form onSubmit={submitFirma} className="form-grid">
              <label className="field">
                <span>Nombre y Apellidos completos del firmante *</span>
                <input
                  required
                  value={firmadoPorNombre}
                  onChange={(e) => setFirmadoPorNombre(e.target.value)}
                  placeholder="Ej: Juan Pérez García"
                />
              </label>

              <label className="field">
                <span>NIF / DNI / NIE / CIF del firmante *</span>
                <input
                  required
                  value={firmadoPorNif}
                  onChange={(e) => setFirmadoPorNif(e.target.value)}
                  placeholder="Ej: 12345678X"
                />
              </label>

              {/* CANVAS PARA DIBUJAR FIRMA */}
              <div className="field span-2">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span>Trazo de Firma Digital * (Dibuja con el dedo o genera una firma con tu nombre)</span>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="btn mini" onClick={generarFirmaDesdeNombre}>
                      Generar firma
                    </button>
                    <button type="button" className="btn mini" onClick={limpiarCanvas}>
                      <FiRotateCcw /> Limpiar
                    </button>
                  </div>
                </div>
                <div style={{ background: '#ffffff', border: '2px dashed #cbd5e1', borderRadius: 10, padding: 4, display: 'inline-block', width: '100%' }}>
                  <canvas
                    ref={canvasRef}
                    width={750}
                    height={160}
                    style={{ width: '100%', height: 160, touchAction: 'none', cursor: 'crosshair', background: '#ffffff', borderRadius: 8 }}
                  />
                </div>
                <small className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
                  {hayTrazo ? (firmaGeneradaNombre ? `✓ Firma generada con “${firmaGeneradaNombre}”.` : '✓ Trazo registrado.') : 'Dibuja con el ratón o firma con el dedo. Si no funciona, usa “Generar firma”.'}
                </small>
              </div>

              {/* CHECKBOX DE ACEPTACIÓN LEGAL */}
              <label className="field checkbox span-2" style={{ alignItems: 'flex-start', gap: 10, marginTop: 8 }}>
                <input
                  type="checkbox"
                  required
                  checked={aceptaTerminos}
                  onChange={(e) => setAceptaTerminos(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span style={{ fontSize: '0.88rem', color: '#334155', lineHeight: 1.4 }}>
                  Declaro que he revisado las condiciones del {esPresupuesto ? 'presupuesto' : 'documento'}, confirmo mi aceptación y consiento la firma digital de un solo uso con pleno valor jurídico legal.
                </span>
              </label>

              <div className="form-actions span-2" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
                <button type="submit" className="btn btn-primary" disabled={enviandoFirma || !hayTrazo || !aceptaTerminos} style={{ padding: '12px 24px', fontSize: '1rem' }}>
                  <FiCheckCircle /> {enviandoFirma ? 'Firmando y registrando…' : 'Firmar Documento Digitalmente'}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: '0.8rem', color: '#94a3b8' }}>
          Plataforma de Firma Electrónica Trix · Seguro · Enlace exclusivo e intransferible
        </div>

      </div>
    </div>
  );
}